import React, {createRef} from 'react';
import {Popup} from 'semantic-ui-react';
import {Scrollbar} from '@priority-software/priority-style-react';
import cx from 'classnames';
import texts from './texts';
import {getFieldRelatedData, getFieldRelatedDataByForm} from './actions';
import {sendMonitor} from '../../utils/monitoring';
import {Loader, MoreInfoColumn, MoreInfoTable, Section} from './components';
import {INFO_MAP, Templates} from './constants';
import {startEntity} from '../../_shared/GWTBridge/invoke/genPage';
import {
    getCollapsedSection,
    getInfoMap,
    saveInfoMap,
    setCollapsedSection,
    updateSelectedFields,
    getMappedFields,
    getDataToStore,
    getDefaultFields
} from './utils/utils';
import {EditingDialog, SelectedEntity} from '../../components/EditingDialog';
import {keyCodes} from '../../constants';
import {showErrorToast} from '../../components/Toast/actions/ToastActions';
import styles from './styles.module.scss';
import {formsStore} from '../../_legacy/Form/stores/FormsStore';
import {MoreInfoNoAccess} from './components/NoAccess';
import {DialogHeader, FormData} from './components/DialogHeader';
import {SuggestionSection} from './components/SuggestionSection';
import {MAX_SHOWN_FORMS} from './components/PanelSettings';
import {ConfirmationDialog} from './components/ConfirmationDialog';
import {startNewCollectionPeriod, rejectMoreInfoSuggestion} from '../Personalization/actions';
import {getTopDialogZindex} from '../../_shared/GWTBridge/invoke/priModalDlg';

type Props = {
    formName: string;
    closeInfo: () => void;
    currentRow?: number;
    currentCell?: number;
    storeMoreInfo: (data: Array<any> | null) => void;
    zoomFname: string;
    fieldName: string;
    zoomValue: string;
    entityType: string;
    entityDescription: string;
    zoomName: string;
};

type State = {
    loading: boolean;
    selectedEntity: SelectedEntity;
    data: Array<FormData>;
    panelTitle?: string;
    entityDescription?: string;
    entityNumber?: string;
    isOpen?: boolean;
    activeSuggestion: boolean;
    suggestion: FormData;
    showConfirmationModal: boolean;
    isSettingsTooltip: boolean;
    defaultFields: string[];
};

export class InfoContent extends React.Component<Props, State> {
    infoMapData = INFO_MAP;
    suggestionSectionRef: React.RefObject<HTMLDivElement>;
    settingsMenuRef: React.RefObject<HTMLDivElement>;
    collapsedSection = getCollapsedSection();
    tooltipTimer: NodeJS.Timeout;
    suggestionTimer: NodeJS.Timeout;

    constructor(props) {
        super(props);
        this.settingsMenuRef = createRef();
        this.suggestionSectionRef = createRef();

        this.state = {
            loading: false,
            selectedEntity: null,
            data: [],
            panelTitle: props.entityType || '',
            entityDescription: props.entityDescription,
            entityNumber: this.props.zoomValue,
            isOpen: true,
            activeSuggestion: false,
            suggestion: null,
            showConfirmationModal: false,
            isSettingsTooltip: false,
            defaultFields: null
        };
    }

    async componentDidMount() {
        window.addEventListener('keydown', this.handleKeyDown, true);
        const currentFieldMoreInfo = formsStore.currentForm?.currentFieldMoreInfo;
        const allFieldsReady =
            currentFieldMoreInfo && currentFieldMoreInfo.every(form => form.fetchValue);
        const isSuggestionData = currentFieldMoreInfo && !allFieldsReady;
        isSuggestionData && this.setState({activeSuggestion: true});

        this.getFieldMoreInfo();
    }

    async componentDidUpdate(prevProps: Props) {
        if (
            this.props.formName !== prevProps.formName ||
            this.props.currentCell !== prevProps.currentCell ||
            this.props.currentRow !== prevProps.currentRow
        ) {
            this.closeInfoPanel();
        }
    }

    componentWillUnmount(): void {
        window.removeEventListener('keydown', this.handleKeyDown, true);
        this.tooltipTimer && clearTimeout(this.tooltipTimer);
        this.suggestionTimer && clearTimeout(this.suggestionTimer);
        if (this.state.activeSuggestion) {
            this.handleChooseClose();
        }
    }

    getFieldMoreInfo = async () => {
        const fetchValue = this.props.zoomValue;
        const zoomName = this.props.zoomName;
        const currentFieldMoreInfo = formsStore.currentForm.currentFieldMoreInfo;

        try {
            if (fetchValue) {
                this.setState({loading: true});

                this.infoMapData = await getInfoMap();
                const supportedEntities =
                    currentFieldMoreInfo || this.infoMapData[this.props.zoomFname];

                const res = await getFieldRelatedData(supportedEntities, {fetchValue, zoomName});

                if (this.state.activeSuggestion) {
                    const data = res.slice(1);
                    formsStore.currentForm.setCurrentFieldMoreInfo(data);
                    this.setState(
                        {loading: false, data, suggestion: res[0]},
                        this.handleApproveSuggestion
                    );
                } else {
                    this.setState({loading: false, data: res});
                }
            } else {
                this.setState({data: []});
            }
        } catch (e) {
            this.setState({loading: false});
            console.error(e);
        }
    };

    renderSectionContent = (records, sectionData) => {
        const {formName, subFormName, template} = sectionData;

        switch (template) {
            case Templates.list:
                return (
                    <MoreInfoTable
                        onShowResults={() => this.onScreenOpen(formName, subFormName)}
                        records={records}
                    />
                );
            case Templates.column:
                return <MoreInfoColumn entityId={sectionData.entityId} data={records[0] || []} />;
            default:
                return <MoreInfoColumn entityId={sectionData.entityId} data={records[0] || []} />;
        }
    };

    onScreenOpen = (formName, subFormName = '') => {
        const cols = formsStore.getMoreInfoCachedCol(formName);
        const fieldName = cols?.Columns.find(col => col.Key)?.Name;
        sendMonitor('MoreDetailsDrilldown', null, fieldName, formName);
        return startEntity(formName, fieldName, this.props.zoomValue, subFormName);
    };

    updateFields = async (entityId: string, selectedFields: Array<string>, cbc?: () => void) => {
        const {data} = this.state;
        const {infoMapData} = this;
        const {currentForm} = formsStore;

        let shouldRefetch = false;
        let updatedData;
        data.forEach(item => {
            if (item.entityId === entityId) {
                shouldRefetch = !selectedFields.every(selectedField => {
                    return item.fields[0].some(field => field.ColumnName === selectedField);
                });
            }
        });

        if (shouldRefetch) {
            const formData = {...data.find(item => item.entityId === entityId), selectedFields};
            updatedData = await getFieldRelatedDataByForm(
                formData,
                {fetchValue: this.props.zoomValue, zoomName: this.props.zoomName},
                entityId
            );
        } else {
            // Prepare the selected fields to store in the local state.
            updatedData = updateSelectedFields(data, entityId, selectedFields);
        }
        // Prepare map data to store on the server.
        const updatedFieldsMap = updateSelectedFields(
            infoMapData[this.props.zoomFname],
            entityId,
            selectedFields
        );

        currentForm.setCurrentFieldMoreInfo(updatedData);

        try {
            this.infoMapData[this.props.zoomFname] = updatedFieldsMap;
            await saveInfoMap(this.infoMapData);
            this.setState({data: updatedData}, cbc);
        } catch (e) {
            showErrorToast((e as Error).message);
        }
    };

    updateInfoMap = async (shownForms: FormData[]) => {
        const fetchValue = this.props.zoomValue;
        const zoomName = this.props.zoomName;
        const updateData = async () => {
            try {
                const updatedData = await getFieldRelatedData(shownForms, {fetchValue, zoomName});
                const dataToStore = getDataToStore(updatedData);
                this.infoMapData[this.props.zoomFname] = dataToStore;
                await saveInfoMap(this.infoMapData);
                this.setState({loading: false, data: updatedData});
            } catch (error) {
                showErrorToast((error as Error).message);
            }
        };

        if (fetchValue) {
            this.setState({loading: true}, updateData);
        } else {
            this.setState({data: []});
        }
    };

    openColumnDialog = (id: string) => {
        const selectedEntity = this.state.data.find(item => item.entityId === id);
        const defaultFields = getDefaultFields(selectedEntity, this.props.zoomFname);
        sendMonitor('MoreDetailsEdit');

        this.setState({selectedEntity, isOpen: true, defaultFields});
    };

    closeDialog = () => {
        this.setState({isOpen: false, selectedEntity: null});
    };

    handleKeyDown = e => {
        if (e.keyCode === keyCodes.ESC) {
            e.stopPropagation();
            this.closeInfoPanel();
        }
    };

    onSectionCollapse = (entityId: string, isOpen: boolean) => {
        const data = getCollapsedSection();
        const currentCollapsed = data[this.props.zoomFname] || [];

        const updatedCurrentCollapsed = isOpen
            ? currentCollapsed.filter(id => id !== entityId)
            : [...currentCollapsed, entityId];

        setCollapsedSection({...data, [this.props.zoomFname]: updatedCurrentCollapsed});
    };

    handleApproveSuggestion = () => {
        const shownForms = this.state.data;
        if (shownForms.length >= MAX_SHOWN_FORMS) {
            const openConfirmationModal = () => {
                this.setState({showConfirmationModal: true});
            };
            this.suggestionTimer = setTimeout(openConfirmationModal, 2000);
        } else {
            sendMonitor('PersonalizationMoreDetailsChooseAutoSave');
            this.applySuggestion(shownForms);
        }
    };

    displaySettingsTooltip = () => {
        const {settingsMenuRef} = this;
        const closeSettingsTooltip = () => {
            this.setState({isSettingsTooltip: false});
        };

        if (settingsMenuRef.current) {
            this.setState({isSettingsTooltip: true});
            this.tooltipTimer = setTimeout(closeSettingsTooltip, 5000);
        }
    };

    resetSuggestion = () => {
        this.suggestionSectionRef = null;
        this.setState(
            {
                activeSuggestion: false,
                suggestion: null,
                showConfirmationModal: false
            },
            this.displaySettingsTooltip
        );
    };

    setResetDelay = () => {
        const milisec = this.state.showConfirmationModal ? 1 : 2000;
        setTimeout(this.resetSuggestion, milisec);
    };

    finalizeSuggestion = (data: FormData[]) => {
        const {zoomFname} = this.props;
        formsStore.currentForm.setCurrentFieldMoreInfo(data);
        const dataToStore = getDataToStore(data);
        this.infoMapData[zoomFname] = dataToStore;
        saveInfoMap(this.infoMapData);
        startNewCollectionPeriod();
        this.setState({data}, this.setResetDelay);
    };

    applySuggestion = (data: FormData[]) => {
        data.unshift(this.state.suggestion);
        this.finalizeSuggestion(data);
    };

    confirmSuggestion = async (entityId: string) => {
        const dataCopy = this.state.data.slice();
        const removedScreenIndex = dataCopy.findIndex(form => form.entityId === entityId);
        dataCopy.splice(removedScreenIndex, 1);
        this.applySuggestion(dataCopy);
    };

    rejectSuggestion = async () => {
        const {data, suggestion} = this.state;
        const dataCopy = data.slice();
        rejectMoreInfoSuggestion(this.props.zoomFname, suggestion.entityId);
        this.finalizeSuggestion(dataCopy);
    };

    handleChooseClose = () => {
        sendMonitor('PersonalizationMoreDetailsChooseClose');
        startNewCollectionPeriod();
        this.resetSuggestion();
    };

    closeInfoPanel = () => {
        if (this.state.showConfirmationModal) {
            this.handleChooseClose();
        }
        this.props.closeInfo();
    };

    render() {
        const {
            data,
            loading,
            entityNumber,
            panelTitle,
            entityDescription,
            suggestion,
            showConfirmationModal,
            isSettingsTooltip
        } = this.state;

        if (loading) return <Loader className={styles.loader} />;
        const disable = suggestion?.entityId.length > 0;

        return (
            <>
                <div className={isSettingsTooltip || disable ? '' : 'dialog-drag-handle'}>
                    <DialogHeader
                        panelTitle={panelTitle}
                        entityDescription={entityDescription}
                        entityNumber={entityNumber}
                        closeInfo={this.closeInfoPanel}
                        shownForms={data}
                        updateInfoMap={this.updateInfoMap}
                        zoomFname={this.props.zoomFname}
                        settingsMenuRef={this.settingsMenuRef}
                        disable={disable}
                    />
                </div>
                <div
                    className={cx(styles.content, {
                        [styles.disabled]: suggestion
                    })}
                >
                    {!data.length && <MoreInfoNoAccess />}
                    {data.length > 0 && (
                        <Scrollbar autoHide={false}>
                            {suggestion && (
                                <SuggestionSection
                                    suggestion={suggestion}
                                    renderSectionContent={this.renderSectionContent}
                                    suggestionSectionRef={this.suggestionSectionRef}
                                />
                            )}
                            {data.map((section, index) => {
                                const isSubForm = !!section.subFormName;

                                const mappedFields = getMappedFields(section);

                                const defaultCollapsed = this.collapsedSection[
                                    this.props.zoomFname
                                ]?.includes(section.entityId);

                                return (
                                    <Section
                                        key={section.entityId}
                                        entityName={
                                            isSubForm ? section.subFormName : section.formName
                                        }
                                        entityId={section.entityId}
                                        title={section.formTitle}
                                        isEmpty={mappedFields.length === 0}
                                        formName={section.formName}
                                        subFormName={section.subFormName}
                                        onScreenOpen={this.onScreenOpen}
                                        defaultCollapsed={defaultCollapsed}
                                        onSectionCollapse={this.onSectionCollapse}
                                        openColumnDialog={this.openColumnDialog}
                                    >
                                        {this.renderSectionContent(mappedFields, section)}
                                    </Section>
                                );
                            })}
                        </Scrollbar>
                    )}
                </div>
                {!!this.state.selectedEntity && (
                    <EditingDialog
                        selectedEntity={this.state.selectedEntity}
                        updateFields={this.updateFields}
                        closeDialog={this.closeDialog}
                        isOpen={this.state.isOpen}
                        zoomFname={this.props.zoomFname}
                        defaultFields={this.state.defaultFields}
                    />
                )}
                {showConfirmationModal && this.suggestionSectionRef.current && (
                    <ConfirmationDialog
                        options={data.map(form => {
                            return {label: form.formTitle, value: form.entityId};
                        })}
                        anchor={this.suggestionSectionRef.current}
                        confirmSuggestion={this.confirmSuggestion}
                        rejectSuggestion={this.rejectSuggestion}
                    />
                )}
                <Popup
                    context={this.settingsMenuRef.current}
                    content={texts.settings_tooltip}
                    position="bottom center"
                    inverted={true}
                    style={{zIndex: getTopDialogZindex()}}
                    open={isSettingsTooltip}
                />
            </>
        );
    }
}
